import type { AddressInformationComponent } from "@redotech/redo-web/concierge/chat-widget-components/address-information-component";
import type { CancelOrderToolMessage } from "@redotech/redo-web/concierge/chat-widget-components/cancel-order-tool-message";
import type { ContactSupport } from "@redotech/redo-web/concierge/chat-widget-components/contact-support";
import type { CreateBundleToolMessage } from "@redotech/redo-web/concierge/chat-widget-components/create-bundle-tool-message";
import type { CustomerSignIn } from "@redotech/redo-web/concierge/chat-widget-components/customer-sign-in";
import type { DiscountCodeBubble } from "@redotech/redo-web/concierge/chat-widget-components/discount-code-bubble";
import type { EmailInputBubble } from "@redotech/redo-web/concierge/chat-widget-components/email-input-bubble";
import type { OrderLookupToolMessage } from "@redotech/redo-web/concierge/chat-widget-components/order-lookup-tool-message";
import type { ProductComparisonTable } from "@redotech/redo-web/concierge/chat-widget-components/product-comparison-table";
import type { ProductVisualizationResult } from "@redotech/redo-web/concierge/chat-widget-components/product-visualization-result";
import type { ProductVisualizationUpload } from "@redotech/redo-web/concierge/chat-widget-components/product-visualization-upload";
import type { SubscribeBackInStockToolMessage } from "@redotech/redo-web/concierge/chat-widget-components/subscribe-back-in-stock-tool-message";
import type { UpdateOrderToolMessage } from "@redotech/redo-web/concierge/chat-widget-components/update-order-tool-message";
import type { WishlistDisplay } from "@redotech/redo-web/concierge/chat-widget-components/wishlist-display";
import type { LookupTrackOrder } from "@redotech/redo-web/concierge/lookup-track-order";
import type { ProductDisplayCarouselWrapper } from "@redotech/redo-web/concierge/product-display-carousel-wrapper";
import {
  type ComponentProps,
  type ComponentType,
  type FC,
  lazy,
  Suspense,
} from "react";
import type { RecentOrdersSection } from "../views/account/recent-orders-section";

const messageFallback = <div style={{ minHeight: "2rem" }}>Loading...</div>;

function lazyMessage<P>(
  load: () => Promise<{ default: ComponentType<any> }>,
): FC<P> {
  const LazyComponent = lazy(load);

  return function LazyMessage(props: any) {
    return (
      <Suspense fallback={messageFallback}>
        <LazyComponent {...props} />
      </Suspense>
    );
  };
}

const asDefault = <T,>(c: T): { default: ComponentType<any> } =>
  ({ default: c }) as any;

export const LazyAddressInformationComponent = lazyMessage<
  ComponentProps<typeof AddressInformationComponent>
>(() =>
  import(
    "@redotech/redo-web/concierge/chat-widget-components/address-information-component"
  ).then((m) => asDefault(m.AddressInformationComponent)),
);

export const LazyCancelOrderToolMessage = lazyMessage<
  ComponentProps<typeof CancelOrderToolMessage>
>(() =>
  import(
    "@redotech/redo-web/concierge/chat-widget-components/cancel-order-tool-message"
  ).then((m) => asDefault(m.CancelOrderToolMessage)),
);

export const LazyContactSupport = lazyMessage<
  ComponentProps<typeof ContactSupport>
>(() =>
  import(
    "@redotech/redo-web/concierge/chat-widget-components/contact-support"
  ).then((m) => asDefault(m.ContactSupport)),
);

export const LazyCreateBundleToolMessage = lazyMessage<
  ComponentProps<typeof CreateBundleToolMessage>
>(() =>
  import(
    "@redotech/redo-web/concierge/chat-widget-components/create-bundle-tool-message"
  ).then((m) => asDefault(m.CreateBundleToolMessage)),
);

export const LazyCustomerSignIn = lazyMessage<
  ComponentProps<typeof CustomerSignIn>
>(() =>
  import(
    "@redotech/redo-web/concierge/chat-widget-components/customer-sign-in"
  ).then((m) => asDefault(m.CustomerSignIn)),
);

export const LazyDiscountCodeBubble = lazyMessage<
  ComponentProps<typeof DiscountCodeBubble>
>(() =>
  import(
    "@redotech/redo-web/concierge/chat-widget-components/discount-code-bubble"
  ).then((m) => asDefault(m.DiscountCodeBubble)),
);

export const LazyEmailInputBubble = lazyMessage<
  ComponentProps<typeof EmailInputBubble>
>(() =>
  import(
    "@redotech/redo-web/concierge/chat-widget-components/email-input-bubble"
  ).then((m) => asDefault(m.EmailInputBubble)),
);

export const LazyOrderLookupToolMessage = lazyMessage<
  ComponentProps<typeof OrderLookupToolMessage>
>(() =>
  import(
    "@redotech/redo-web/concierge/chat-widget-components/order-lookup-tool-message"
  ).then((m) => asDefault(m.OrderLookupToolMessage)),
);

export const LazyProductComparisonTable = lazyMessage<
  ComponentProps<typeof ProductComparisonTable>
>(() =>
  import(
    "@redotech/redo-web/concierge/chat-widget-components/product-comparison-table"
  ).then((m) => asDefault(m.ProductComparisonTable)),
);

export const LazyProductVisualizationResult = lazyMessage<
  ComponentProps<typeof ProductVisualizationResult>
>(() =>
  import(
    "@redotech/redo-web/concierge/chat-widget-components/product-visualization-result"
  ).then((m) => asDefault(m.ProductVisualizationResult)),
);

export const LazyProductVisualizationUpload = lazyMessage<
  ComponentProps<typeof ProductVisualizationUpload>
>(() =>
  import(
    "@redotech/redo-web/concierge/chat-widget-components/product-visualization-upload"
  ).then((m) => asDefault(m.ProductVisualizationUpload)),
);

export const LazySubscribeBackInStockToolMessage = lazyMessage<
  ComponentProps<typeof SubscribeBackInStockToolMessage>
>(() =>
  import(
    "@redotech/redo-web/concierge/chat-widget-components/subscribe-back-in-stock-tool-message"
  ).then((m) => asDefault(m.SubscribeBackInStockToolMessage)),
);

export const LazyUpdateOrderToolMessage = lazyMessage<
  ComponentProps<typeof UpdateOrderToolMessage>
>(() =>
  import(
    "@redotech/redo-web/concierge/chat-widget-components/update-order-tool-message"
  ).then((m) => asDefault(m.UpdateOrderToolMessage)),
);

export const LazyWishlistDisplay = lazyMessage<
  ComponentProps<typeof WishlistDisplay>
>(() =>
  import(
    "@redotech/redo-web/concierge/chat-widget-components/wishlist-display"
  ).then((m) => asDefault(m.WishlistDisplay)),
);

export const LazyLookupTrackOrder = lazyMessage<
  ComponentProps<typeof LookupTrackOrder>
>(() =>
  import("@redotech/redo-web/concierge/lookup-track-order").then((m) =>
    asDefault(m.LookupTrackOrder),
  ),
);

export const LazyProductDisplayCarouselWrapper = lazyMessage<
  ComponentProps<typeof ProductDisplayCarouselWrapper>
>(() =>
  import("@redotech/redo-web/concierge/product-display-carousel-wrapper").then(
    (m) => asDefault(m.ProductDisplayCarouselWrapper),
  ),
);

export const LazyRecentOrdersSection = lazyMessage<
  ComponentProps<typeof RecentOrdersSection>
>(() =>
  import("../views/account/recent-orders-section").then((m) =>
    asDefault(m.RecentOrdersSection),
  ),
);
